import { http } from '@/lib/ats-https';
import { getAssessmentFormData } from '@/store/query/assessment/utils';
import { PlatformLogical } from '@/types/ext_assessment';

export interface InviteAssessmentPayload {
  assessmentId: string;
  ats_job_stage_id: string;
  ats_job_stage_action_id: string;
  ats_application_id: string;
  params: {
    ext_test_name?: string;
    ext_assessment_link?: string;
    feedback_description?: string;
    rating_description?: string;
    scoreCards?: Record<string, unknown>[];
    platform?: string;
    kind?: string;
    [key: string]: unknown;
  };
  platform: PlatformLogical;
  [key: string]: unknown;
}

export interface CreateAssessmentPayload {
  ats_job_stage_action_id: string;
  ats_application_id: string;
  platform: PlatformLogical;
}

// Consolidated endpoints - platform-agnostic
const ASSESSMENT_ENDPOINTS = {
  invite: '/ats_assessments/invite_external_assessment',
  inviteAdhoc: '/ats_assessments/invite_adhoc_assessment',
  delete: (id: string) => `/ats_assessments/${id}`,
  generateReport: '/ats_assessments/generate_report',
};

export interface InviteBulkHireflixPayload {
  ats_job_stage_id: string;
  ext_test_id: string;
  ext_test_name: string;
  ats_application_ids: string[];
  participant_ids: string[];
  is_bulk_activity?: boolean;
  ats_job_id?: number;
}

export interface GetPositionStatusPayload {
  ats_job_stage_id: string;
  ats_application_ids: string[];
}

export const ASSESSMENT_API = {
  createAssessment: async (payload: CreateAssessmentPayload) => {
    const response = await http.post('/ats_assessments', payload);
    return response.data;
  },

  getAssessment: async (assessmentId: string, platform: PlatformLogical) => {
    const response = await http.get(
      `/ats_assessments/${assessmentId}?platform=${platform}`
    );
    return response.data;
  },

  deleteAssessment: async (assessmentId: string, platform: PlatformLogical) => {
    const endpoint = ASSESSMENT_ENDPOINTS.delete(assessmentId);
    const response = await http.delete(endpoint, {
      data: { platform },
    });
    return response;
  },

  generateReport: async (assessmentId: string, platform: PlatformLogical) => {
    const endpoint = ASSESSMENT_ENDPOINTS.generateReport;
    const response = await http.post(endpoint, {
      id: assessmentId,
      platform,
    });
    return response;
  },

  inviteAssessment: async (payload: InviteAssessmentPayload) => {
    const {
      assessmentId,
      ats_job_stage_id,
      ats_job_stage_action_id,
      ats_application_id,
      params,
      platform,
      ...rest
    } = payload;

    const fd = new FormData();

    fd.append('id', assessmentId);
    fd.append('ats_job_stage_id', ats_job_stage_id);
    fd.append('ats_job_stage_action_id', ats_job_stage_action_id);
    fd.append('ats_application_id', ats_application_id);
    fd.append('platform', platform);

    getAssessmentFormData(fd, params);

    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fd.append(key, value as string | Blob);
      }
    });

    const endpoint = ASSESSMENT_ENDPOINTS.invite;
    const response = await http.post(endpoint, fd);
    return response;
  },

  inviteAdhocAssessment: async (payload: InviteAssessmentPayload) => {
    const {
      ats_job_stage_id,
      ats_job_stage_action_id,
      ats_application_id,
      params,
      platform,
      ...rest
    } = payload;

    const fd = new FormData();

    fd.append('ats_job_stage_id', ats_job_stage_id);
    fd.append('ats_job_stage_action_id', ats_job_stage_action_id);
    fd.append('ats_application_id', ats_application_id);
    fd.append('platform', platform);

    getAssessmentFormData(fd, params);

    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fd.append(key, value as string | Blob);
      }
    });

    const endpoint = ASSESSMENT_ENDPOINTS.inviteAdhoc;
    const response = await http.post(endpoint, fd);
    return response;
  },

  inviteBulkHireflix: async (payload: InviteBulkHireflixPayload) => {
    const response = await http.post(
      '/ats_assessments/bulk/invite_hireflix',
      payload
    );
    return response;
  },

  getPositionStatus: async (payload: GetPositionStatusPayload) => {
    const params = new URLSearchParams();
    params.append('ats_job_stage_id', payload.ats_job_stage_id);
    payload.ats_application_ids.forEach((id) => {
      params.append('ats_application_ids[]', id.toString());
    });
    const response = await http.get(
      `/ats_assessments/bulk/position_status?${params.toString()}`
    );
    return response.data;
  },
};
